/**
 * «Поделиться текстом» (handoff §17.1, artboard 19; ADR-115 Decision 10).
 *
 * Without a link: what the link shows and what it never shows, and «Создать ссылку».
 * With one: the link (`{origin}/t/{token}`), «Копировать», «Отправить…» where the
 * system share sheet exists, the date it was made, a new link, and turning it off.
 *
 * A text has one live link at a time and every share mints a new one, which kills the
 * old: «Создать новую ссылку…» says so and asks before replacing. Turning it off asks
 * too. Neither touches the copies others already saved.
 *
 * Opened from the text screen and from a row's «···». The link state is the text read's
 * `share`, fetched here when a list row opens it.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, Copy, Link2, Share2 } from 'lucide-react'
import { useLocale } from '../../../i18n/context'
import { useText } from '../../../hooks/useTexts'
import { useIsDesktop } from '../../../hooks/useIsDesktop'
import { useCreateTextShare, useRevokeTextShare } from '../../../hooks/useTextShare'
import { whenStarted } from '../../../utils/textPractice'
import { toast } from '../../shared/Toast'
import { GhostButton, PrimaryButton, SecondaryPill } from '../ui'
import SheetFrame, { SheetActions } from './SheetFrame'

type Step = 'main' | 'confirmNew' | 'confirmRevoke'

const COPIED_MS = 2000

const shareUrl = (token: string) => `${window.location.origin}/t/${token}`

const canWebShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function'

export default function TextShareSheet({ textId, onClose }: { textId: string; onClose: () => void }) {
  const { tr, tag } = useLocale()
  const t = tr.textShare
  const desktop = useIsDesktop()
  const { data: text, isLoading, isError, refetch } = useText(textId)
  const create = useCreateTextShare(textId)
  const revoke = useRevokeTextShare(textId)
  const [step, setStep] = useState<Step>('main')
  const [failed, setFailed] = useState(false)
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<number | null>(null)
  const linkRef = useRef<HTMLInputElement>(null)
  useEffect(() => () => { if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current) }, [])

  const share = text?.share ?? null
  const url = share ? shareUrl(share.token) : ''
  const busy = create.isPending || revoke.isPending

  const makeLink = () => {
    setFailed(false)
    create.mutate(undefined, {
      onSuccess: () => { setStep('main'); setCopied(false) },
      onError: () => setFailed(true),
    })
  }
  const turnOff = () => {
    setFailed(false)
    revoke.mutate(undefined, {
      onSuccess: () => { setStep('main'); toast.success(t.revoked) },
      onError: () => setFailed(true),
    })
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // No clipboard permission: the link is selected, so the system's own copy works.
      linkRef.current?.select()
      return
    }
    setCopied(true)
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current)
    copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_MS)
  }
  const send = () => {
    navigator.share?.({ title: text?.title, url }).catch(() => { /* dismissed: nothing to say */ })
  }

  const errorLine = failed && (
    <p role="alert" data-testid="share-failed" style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--danger)' }}>{t.failed}</p>
  )
  const secondaryButton = (label: string, onClick: () => void, testId: string) => (
    <SecondaryPill full={!desktop} height={desktop ? 40 : 48} onClick={onClick} data-testid={testId}>{label}</SecondaryPill>
  )

  if (step === 'confirmNew') {
    return (
      <SheetFrame title={t.newTitle} onClose={() => setStep('main')} closeLabel={t.close} testId="share-new-confirm">
        <p style={bodyStyle}>{t.newBody}</p>
        {errorLine}
        <SheetActions>
          <PrimaryButton full={!desktop} height={desktop ? 40 : 52} onClick={makeLink} disabled={busy} data-testid="share-new-confirm-go">
            {create.isPending ? t.creating : t.newConfirm}
          </PrimaryButton>
          {secondaryButton(t.cancel, () => setStep('main'), 'share-new-cancel')}
        </SheetActions>
      </SheetFrame>
    )
  }

  if (step === 'confirmRevoke') {
    return (
      <SheetFrame title={t.revokeTitle} onClose={() => setStep('main')} closeLabel={t.close} testId="share-revoke-confirm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={bodyStyle}>{t.revokeBody}</p>
          <p style={bodyStyle}>{t.revokeNote}</p>
        </div>
        {errorLine}
        <SheetActions>
          <PrimaryButton
            full={!desktop}
            height={desktop ? 40 : 52}
            onClick={turnOff}
            disabled={busy}
            data-testid="share-revoke-go"
            style={busy ? undefined : { background: 'var(--danger-fill)', color: 'var(--text-on-dark)' }}
          >
            {t.revokeConfirm}
          </PrimaryButton>
          {secondaryButton(t.cancel, () => setStep('main'), 'share-revoke-cancel')}
        </SheetActions>
      </SheetFrame>
    )
  }

  if (isLoading || (isError && !text)) {
    return (
      <SheetFrame title={t.sheetTitle} onClose={onClose} closeLabel={t.close} testId="share-sheet" showClose>
        {isLoading ? <div aria-busy="true" style={{ height: 120 }} /> : (
          <div role="alert" style={{ padding: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
            <p style={bodyStyle}>{t.failed}</p>
            <SecondaryPill onClick={() => refetch()}>{tr.textScreen.retry}</SecondaryPill>
          </div>
        )}
      </SheetFrame>
    )
  }

  if (!share) {
    return (
      <SheetFrame title={t.sheetTitle} onClose={onClose} closeLabel={t.close} testId="share-sheet">
        <div data-testid="share-sheet-none" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ ...bodyStyle, fontSize: 14 }}>{t.intro}</p>
          <div style={{ padding: '12px 14px', borderRadius: 16, background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-primary)' }}>
              <span style={{ fontWeight: 600 }}>{t.seeTitle}</span> — {t.seeBody}
            </p>
            <p data-testid="share-hidden-line" style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-primary)' }}>{t.hiddenLine}</p>
            <p data-testid="share-copy-note" style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{t.copyNote}</p>
          </div>
        </div>
        {errorLine}
        <SheetActions>
          <PrimaryButton full={!desktop} height={desktop ? 40 : 52} onClick={makeLink} disabled={busy} data-testid="share-create">
            <Link2 size={18} aria-hidden />
            {create.isPending ? t.creating : t.create}
          </PrimaryButton>
          {secondaryButton(t.cancel, onClose, 'share-cancel')}
        </SheetActions>
      </SheetFrame>
    )
  }

  const webShare = canWebShare()
  return (
    <SheetFrame title={t.sheetTitle} onClose={onClose} closeLabel={t.close} testId="share-sheet" showClose>
      <div data-testid="share-sheet-link" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p data-testid="share-enabled" style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
          {t.enabled(whenStarted(share.created_at, tr, tag))}
        </p>
        {/* Read-only and selectable: the link is the one thing on the sheet meant to be copied by hand. */}
        <input
          ref={linkRef}
          readOnly
          value={url}
          aria-label={t.linkLabel}
          data-testid="share-link"
          onFocus={(e) => e.currentTarget.select()}
          style={{
            width: '100%', height: 48, boxSizing: 'border-box', padding: '0 14px', borderRadius: 8,
            border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            fontSize: 15, fontFamily: 'var(--font-ui)', textOverflow: 'ellipsis', userSelect: 'text', WebkitUserSelect: 'text',
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <SecondaryPill
            height={44}
            onClick={copy}
            data-testid="share-copy"
            style={{ flex: 1, ...(copied ? { color: 'var(--accent)' } : null) }}
          >
            {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
            {copied ? t.copied : t.copy}
          </SecondaryPill>
          {webShare && (
            <PrimaryButton height={44} onClick={send} data-testid="share-send" style={{ flex: 1, padding: '0 16px', fontSize: 14 }}>
              <Share2 size={16} aria-hidden />
              {t.send}
            </PrimaryButton>
          )}
        </div>
        {errorLine}
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <GhostButton onClick={() => { setFailed(false); setStep('confirmNew') }} data-testid="share-new" style={{ padding: 0 }}>
            {t.newLink}
          </GhostButton>
          <GhostButton onClick={() => { setFailed(false); setStep('confirmRevoke') }} data-testid="share-revoke" style={{ padding: 0, color: 'var(--danger)' }}>
            {t.revoke}
          </GhostButton>
        </div>
      </div>
    </SheetFrame>
  )
}

const bodyStyle: React.CSSProperties = { margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)' }
